import { useState, useCallback } from "react"

// Función para obtener el token CSRF
const getCSRFToken = () => {
  const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
  return token
}

const useVideoUpload = () => {
  const [uploadState, setUploadState] = useState({
    isUploading: false,
    uploadProgress: 0,
    isTranscoding: false,
    error: null,
    videoData: null
  })

  // Reset del estado
  const resetUploadState = useCallback(() => {
    setUploadState({
      isUploading: false,
      uploadProgress: 0,
      isTranscoding: false,
      error: null,
      videoData: null
    })
  }, [])

  // Obtener presigned URL del backend
  const getPresignedUrl = useCallback(async (file) => {
    try {
      const response = await fetch(
        "/api/videos/presigned_url?" +
          new URLSearchParams({
            filename: file.name,
            content_type: file.type
          }),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json"
            // Agrega tus headers de autenticación aquí
            // 'Authorization': `Bearer ${token}`,
          },
          credentials: "include" // Para cookies de sesión
        }
      )

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error || "Failed to get upload URL")
      }

      return data.data
    } catch (error) {
      console.error("Error getting presigned URL:", error) // eslint-disable-line no-console
      throw error
    }
  }, [])

  // Subir archivo a S3 usando presigned URL
  const uploadToS3 = useCallback(
    async (file, presignedData) =>
      new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()

        // Manejar progreso del upload
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            const percentComplete = (event.loaded / event.total) * 100
            setUploadState((prev) => ({
              ...prev,
              uploadProgress: Math.round(percentComplete)
            }))
          }
        })

        // Manejar resultado del upload
        xhr.addEventListener("load", () => {
          if (xhr.status === 200) {
            resolve(presignedData.video_key)
          } else {
            reject(new Error(`Upload failed with status: ${xhr.status}`))
          }
        })

        xhr.addEventListener("error", () => {
          reject(new Error("Upload failed due to network error"))
        })

        // Configurar y enviar request
        xhr.open("PUT", presignedData.upload_url)
        xhr.setRequestHeader("Content-Type", file.type)
        xhr.send(file)
      }),
    []
  )

  // Avisar al backend que el video está listo para transcodificar
  const startTranscoding = useCallback(async (videoKey) => {
    try {
      const csrfToken = getCSRFToken()
      const response = await fetch("/api/videos/start_transcoding", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken
        },
        credentials: "include", // Para cookies de sesión
        body: JSON.stringify({ video_key: videoKey })
      })

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error || "Failed to start transcoding")
      }

      return data.data
    } catch (error) {
      console.error("Error starting transcoding:", error) // eslint-disable-line no-console
      throw error
    }
  }, [])

  // Verificar estatus del job de MediaConvert
  const checkJobStatus = useCallback(async (jobId) => {
    try {
      const response = await fetch(`/api/videos/${jobId}/status`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json"
          // Agrega tus headers de autenticación aquí
          // 'Authorization': `Bearer ${token}`,
        },
        credentials: "include" // Para cookies de sesión
      })

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error || "Failed to check job status")
      }

      return data.data
    } catch (error) {
      console.error("Error checking job status:", error) // eslint-disable-line no-console
      throw error
    }
  }, [])

  // Función principal para subir video
  const uploadVideo = useCallback(
    async (file) => {
      try {
        // Validaciones iniciales
        if (!file) {
          throw new Error("No file selected")
        }

        // Validar tipo de archivo
        const allowedTypes = ["video/mp4", "video/quicktime", "video/x-msvideo", "video/webm"]
        if (!allowedTypes.includes(file.type)) {
          throw new Error("File type not supported. Please use MP4, MOV, AVI, or WebM.")
        }

        // Validar tamaño (100MB max)
        const maxSize = 100 * 1024 * 1024 // 100MB
        if (file.size > maxSize) {
          throw new Error("File size too large. Maximum size is 100MB.")
        }

        // Iniciar upload
        setUploadState((prev) => ({
          ...prev,
          isUploading: true,
          uploadProgress: 0,
          error: null
        }))

        // 1. Obtener presigned URL
        const presignedData = await getPresignedUrl(file)

        // 2. Subir archivo a S3
        const videoKey = await uploadToS3(file, presignedData)

        // 3. Actualizar estado después del upload
        setUploadState((prev) => ({
          ...prev,
          isUploading: false,
          isTranscoding: true,
          videoData: { video_key: videoKey }
        }))

        // 4. Iniciar transcodificación
        const transcodingData = await startTranscoding(videoKey)

        // 5. Finalizar
        setUploadState((prev) => ({
          ...prev,
          isTranscoding: false,
          videoData: {
            ...prev.videoData,
            job_id: transcodingData.job_id,
            status: transcodingData.status
          }
        }))

        return {
          video_key: videoKey,
          job_id: transcodingData.job_id,
          status: transcodingData.status
        }
      } catch (error) {
        setUploadState((prev) => ({
          ...prev,
          isUploading: false,
          isTranscoding: false,
          error: error.message
        }))
        throw error
      }
    },
    [getPresignedUrl, uploadToS3, startTranscoding]
  )

  return {
    uploadState,
    uploadVideo,
    checkJobStatus,
    resetUploadState
  }
}

export default useVideoUpload
